import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Package, Plus, Edit, Trash2, Upload, Download, BarChart3, 
  PieChart, RefreshCw, Search, Filter, Laptop, Monitor, 
  Headphones, Smartphone, Server, HardDrive, Printer, X
} from 'lucide-react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const STATUS_COLORS = {
  available: 'bg-green-500',
  in_use: 'bg-blue-500',
  under_maintenance: 'bg-yellow-500',
  retired: 'bg-gray-500',
  lost: 'bg-red-500'
};

const AssetManagement = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [assets, setAssets] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  
  // Dashboard chart selection
  const [selectedCharts, setSelectedCharts] = useState(['by_type', 'by_status', 'by_department']);
  
  // Form state
  const [assetForm, setAssetForm] = useState({
    asset_id: '',
    asset_type: '',
    serial_number: '',
    manufacturer: '',
    model: '',
    purchase_date: '',
    location: '',
    assigned_to_name: '',
    assigned_to_emp_id: '',
    department: '',
    status: 'available',
    asset_tag_number: '',
    software_installed: '',
    device_usage_type: '',
    support_tier: '',
    operating_system: '',
    ip_address: '',
    mac_address: '',
    notes: ''
  });

  // Bulk upload state
  const [uploadData, setUploadData] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/assets`, config);
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/assets/dashboard`, config);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
    fetchDashboard();
  }, [fetchAssets, fetchDashboard]);

  const handleCreateAsset = async () => {
    if (!assetForm.asset_id || !assetForm.asset_type) {
      toast.error('Asset ID and Type are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editingAsset) {
        await axios.put(`${API}/assets/${editingAsset.id}`, assetForm, config);
        toast.success('Asset updated successfully');
      } else {
        await axios.post(`${API}/assets`, assetForm, config);
        toast.success('Asset created successfully');
      }
      
      setShowAssetDialog(false);
      resetForm();
      fetchAssets();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/assets/${assetId}`, config);
      toast.success('Asset deleted successfully');
      fetchAssets();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      asset_id: asset.asset_id || '',
      asset_type: asset.asset_type || '',
      serial_number: asset.serial_number || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      purchase_date: asset.purchase_date || '',
      location: asset.location || '',
      assigned_to_name: asset.assigned_to_name || '',
      assigned_to_emp_id: asset.assigned_to_emp_id || '',
      department: asset.department || '',
      status: asset.status || 'available',
      asset_tag_number: asset.asset_tag_number || '',
      software_installed: asset.software_installed || '',
      device_usage_type: asset.device_usage_type || '',
      support_tier: asset.support_tier || '',
      operating_system: asset.operating_system || '',
      ip_address: asset.ip_address || '',
      mac_address: asset.mac_address || '',
      notes: asset.notes || ''
    });
    setShowAssetDialog(true);
  };

  const resetForm = () => {
    setEditingAsset(null);
    setAssetForm({
      asset_id: '',
      asset_type: '',
      serial_number: '',
      manufacturer: '',
      model: '',
      purchase_date: '',
      location: '',
      assigned_to_name: '',
      assigned_to_emp_id: '',
      department: '',
      status: 'available',
      asset_tag_number: '',
      software_installed: '',
      device_usage_type: '',
      support_tier: '',
      operating_system: '',
      ip_address: '',
      mac_address: '',
      notes: ''
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        
        // Handle CSV with quoted fields containing commas
        const values = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
        const row = {};
        
        headers.forEach((header, index) => {
          let value = values[index] || '';
          // Remove surrounding quotes
          value = value.replace(/^"|"$/g, '').trim();
          row[header] = value;
        });
        
        data.push(row);
      }
      
      setUploadData(data);
      toast.success(`Parsed ${data.length} rows from CSV`);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (uploadData.length === 0) {
      toast.error('No data to upload');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/assets/bulk-upload`, { assets: uploadData }, config);
      
      toast.success(response.data.message);
      if (response.data.errors.length > 0) {
        console.error('Asset bulk-upload errors:', response.data.errors);
      }
      
      setShowUploadDialog(false);
      setUploadData([]);
      fetchAssets();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const headers = 'asset_id,asset_type,serial_number,manufacturer,model,purchase_date,location,assigned_to Name,EMP ID,department,status,asset_tag_number,software_installed,device_usage_type,support_tier,operating_system,ip_address,mac_address,notes';
    const sample = 'AS0001,Laptop,SN123456789,Apple,MacBook Pro 16-inch,1/15/2024,New York Office,John Doe,user-123,IT Department,in_use,TAG001,Office Suite | Development Tools,work_only,standard,macOS Sonoma,192.168.1.100,00:1B:44:11:3A:B7,Sample laptop';
    const csv = headers + '\n' + sample;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleChart = (chartKey) => {
    setSelectedCharts(prev => {
      if (prev.includes(chartKey)) {
        return prev.filter(c => c !== chartKey);
      }
      return [...prev, chartKey];
    });
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = 
      asset.asset_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.asset_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assigned_to_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || asset.asset_type === filterType;
    const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Get unique types for filter
  const assetTypes = [...new Set(assets.map(a => a.asset_type).filter(Boolean))];

  const getAssetIcon = (type) => {
    const iconMap = {
      'Laptop': <Laptop className="w-4 h-4" />,
      'Desktop': <Monitor className="w-4 h-4" />,
      'Headset': <Headphones className="w-4 h-4" />,
      'Phone': <Smartphone className="w-4 h-4" />,
      'Server': <Server className="w-4 h-4" />,
      'Storage': <HardDrive className="w-4 h-4" />,
      'Printer': <Printer className="w-4 h-4" />
    };
    return iconMap[type] || <Package className="w-4 h-4" />;
  };

  const chartOptions = [
    { key: 'by_type', label: 'By Type', type: 'pie' },
    { key: 'by_status', label: 'By Status', type: 'pie' },
    { key: 'by_department', label: 'By Department', type: 'bar' },
    { key: 'by_location', label: 'By Location', type: 'bar' },
    { key: 'by_manufacturer', label: 'By Manufacturer', type: 'pie' },
    { key: 'by_assignee', label: 'Top Assignees', type: 'bar' }
  ];

  const renderChart = (chartKey) => {
    if (!dashboardData || !dashboardData[chartKey]) return null;
    
    const data = dashboardData[chartKey];
    const option = chartOptions.find(o => o.key === chartKey);
    
    if (option?.type === 'pie') {
      return (
        <Card key={chartKey} className="col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              {option.label}
              <Button variant="ghost" size="sm" onClick={() => toggleChart(chartKey)}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <RechartsPieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card key={chartKey} className="col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            {option?.label}
            <Button variant="ghost" size="sm" onClick={() => toggleChart(chartKey)}>
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-purple-600" />
            Asset Management
          </h2>
          <p className="text-gray-500">Manage IT assets, track assignments, and view analytics</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchAssets(); fetchDashboard(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          <Button onClick={() => { resetForm(); setShowAssetDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="dashboard" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="list" className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            Assets ({assets.length})
          </TabsTrigger>
        </TabsList>

        {/* Dashboard View */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Assets</p>
                    <p className="text-3xl font-bold text-purple-600">{dashboardData?.total_assets || 0}</p>
                  </div>
                  <Package className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">In Use</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {dashboardData?.by_status?.find(s => s.name === 'in_use')?.value || 0}
                    </p>
                  </div>
                  <Monitor className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Available</p>
                    <p className="text-3xl font-bold text-green-600">
                      {dashboardData?.by_status?.find(s => s.name === 'available')?.value || 0}
                    </p>
                  </div>
                  <Package className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Maintenance</p>
                    <p className="text-3xl font-bold text-yellow-600">
                      {dashboardData?.by_status?.find(s => s.name === 'under_maintenance')?.value || 0}
                    </p>
                  </div>
                  <Server className="w-10 h-10 text-yellow-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart Selection */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Select Charts to Display</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {chartOptions.map(option => (
                  <Button
                    key={option.key}
                    variant={selectedCharts.includes(option.key) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleChart(option.key)}
                  >
                    {option.type === 'pie' ? <PieChart className="w-3 h-3 mr-1" /> : <BarChart3 className="w-3 h-3 mr-1" />}
                    {option.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Charts Grid */}
          <div className="grid grid-cols-3 gap-4">
            {selectedCharts.map(chartKey => renderChart(chartKey))}
          </div>
        </TabsContent>

        {/* Assets List View */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search by ID, type, assignee, serial number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {assetTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="in_use">In Use</SelectItem>
                    <SelectItem value="under_maintenance">Maintenance</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Assets Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset ID</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No assets found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssets.map(asset => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-mono font-medium">{asset.asset_id}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getAssetIcon(asset.asset_type)}
                            {asset.asset_type}
                          </div>
                        </TableCell>
                        <TableCell>{asset.manufacturer || '-'}</TableCell>
                        <TableCell>{asset.model || '-'}</TableCell>
                        <TableCell>
                          {asset.assigned_to_name || <span className="text-gray-400">Unassigned</span>}
                          {asset.assigned_to_emp_id && (
                            <span className="text-xs text-gray-400 block">{asset.assigned_to_emp_id}</span>
                          )}
                        </TableCell>
                        <TableCell>{asset.department || '-'}</TableCell>
                        <TableCell>{asset.location || '-'}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[asset.status] || 'bg-gray-500'}>
                            {asset.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-center">
                            <Button variant="ghost" size="sm" onClick={() => handleEditAsset(asset)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAsset(asset.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Asset Dialog */}
      <Dialog open={showAssetDialog} onOpenChange={(open) => { setShowAssetDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            <DialogDescription>
              {editingAsset ? 'Update the asset information below' : 'Fill in the details to add a new asset'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Asset ID *</Label>
              <Input
                value={assetForm.asset_id}
                onChange={(e) => setAssetForm({ ...assetForm, asset_id: e.target.value })}
                placeholder="AS0001"
                disabled={editingAsset}
              />
            </div>
            <div>
              <Label>Asset Type *</Label>
              <Select value={assetForm.asset_type} onValueChange={(v) => setAssetForm({ ...assetForm, asset_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Laptop">Laptop</SelectItem>
                  <SelectItem value="Desktop">Desktop</SelectItem>
                  <SelectItem value="Monitor">Monitor</SelectItem>
                  <SelectItem value="Headset">Headset</SelectItem>
                  <SelectItem value="Phone">Phone</SelectItem>
                  <SelectItem value="Server">Server</SelectItem>
                  <SelectItem value="Printer">Printer</SelectItem>
                  <SelectItem value="Storage">Storage</SelectItem>
                  <SelectItem value="Networking">Networking</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={assetForm.status} onValueChange={(v) => setAssetForm({ ...assetForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in_use">In Use</SelectItem>
                  <SelectItem value="under_maintenance">Under Maintenance</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Serial Number</Label>
              <Input
                value={assetForm.serial_number}
                onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
              />
            </div>
            <div>
              <Label>Manufacturer</Label>
              <Input
                value={assetForm.manufacturer}
                onChange={(e) => setAssetForm({ ...assetForm, manufacturer: e.target.value })}
              />
            </div>
            <div>
              <Label>Model</Label>
              <Input
                value={assetForm.model}
                onChange={(e) => setAssetForm({ ...assetForm, model: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Purchase Date</Label>
              <Input
                type="date"
                value={assetForm.purchase_date}
                onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={assetForm.location}
                onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })}
              />
            </div>
            <div>
              <Label>Asset Tag Number</Label>
              <Input
                value={assetForm.asset_tag_number}
                onChange={(e) => setAssetForm({ ...assetForm, asset_tag_number: e.target.value })}
              />
            </div>
            
            <div className="col-span-3 border-t pt-4 mt-2">
              <p className="font-medium text-sm text-gray-500 mb-3">Assignment Details</p>
            </div>
            
            <div>
              <Label>Assigned To (Name)</Label>
              <Input
                value={assetForm.assigned_to_name}
                onChange={(e) => setAssetForm({ ...assetForm, assigned_to_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Employee ID</Label>
              <Input
                value={assetForm.assigned_to_emp_id}
                onChange={(e) => setAssetForm({ ...assetForm, assigned_to_emp_id: e.target.value })}
              />
            </div>
            <div>
              <Label>Department</Label>
              <Input
                value={assetForm.department}
                onChange={(e) => setAssetForm({ ...assetForm, department: e.target.value })}
              />
            </div>
            
            <div className="col-span-3 border-t pt-4 mt-2">
              <p className="font-medium text-sm text-gray-500 mb-3">Technical Details</p>
            </div>
            
            <div>
              <Label>Operating System</Label>
              <Input
                value={assetForm.operating_system}
                onChange={(e) => setAssetForm({ ...assetForm, operating_system: e.target.value })}
              />
            </div>
            <div>
              <Label>IP Address</Label>
              <Input
                value={assetForm.ip_address}
                onChange={(e) => setAssetForm({ ...assetForm, ip_address: e.target.value })}
              />
            </div>
            <div>
              <Label>MAC Address</Label>
              <Input
                value={assetForm.mac_address}
                onChange={(e) => setAssetForm({ ...assetForm, mac_address: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Device Usage Type</Label>
              <Select value={assetForm.device_usage_type} onValueChange={(v) => setAssetForm({ ...assetForm, device_usage_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="work_only">Work Only</SelectItem>
                  <SelectItem value="personal_allowed">Personal Allowed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Support Tier</Label>
              <Select value={assetForm.support_tier} onValueChange={(v) => setAssetForm({ ...assetForm, support_tier: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Software Installed</Label>
              <Input
                value={assetForm.software_installed}
                onChange={(e) => setAssetForm({ ...assetForm, software_installed: e.target.value })}
                placeholder="Office Suite | Dev Tools"
              />
            </div>
            
            <div className="col-span-3">
              <Label>Notes</Label>
              <Textarea
                value={assetForm.notes}
                onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAsset} className="bg-purple-600 hover:bg-purple-700">
              {editingAsset ? 'Update Asset' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Assets</DialogTitle>
            <DialogDescription>
              Upload a CSV file to add multiple assets at once
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Click to upload or drag and drop</p>
                <p className="text-sm text-gray-400">CSV file only</p>
              </label>
            </div>
            
            {uploadData.length > 0 && (
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 font-medium">{uploadData.length} rows ready to upload</p>
                <p className="text-sm text-green-600">Preview: {uploadData.slice(0, 3).map(r => r.asset_id).join(', ')}...</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadData([]); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpload} 
              disabled={uploadData.length === 0 || uploading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploading ? 'Uploading...' : `Upload ${uploadData.length} Assets`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetManagement;
